import { Slot, Slottable } from "@radix-ui/react-slot";
import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { focusRingStyles } from "../styles";

interface BadgeProps extends React.ComponentPropsWithoutRef<"span">, BadgeVariants {
  icon?: React.ReactElement;
  asChild?: boolean;
}

export const Badge = React.forwardRef<HTMLElement, BadgeProps>(
  ({ interactive, colorScheme, variant, shape, size, icon, children, className, asChild, ...rest }, ref) => {
    const Component = asChild ? Slot : "span";
    const iconElement = icon
      ? React.cloneElement(icon, {
          className: badgeIconVariants({ size }),
        })
      : null;
    const elementVars = COLOR_SCHEME_VARS[colorScheme ?? "neutral"][variant ?? "subtle"];

    return (
      <Component
        ref={ref}
        {...rest}
        className={badgeVariants({
          interactive,
          colorScheme,
          variant,
          shape,
          size,
          className: [elementVars.bg, elementVars.text, className],
        })}
      >
        {iconElement}
        <Slottable>{children}</Slottable>
      </Component>
    );
  },
);

Badge.displayName = "Badge";

const badgeVariants = tv({
  base: "no-underline flex shrink-0 items-center tabular-nums font-medium transition-colors bg-(--bg-color) text-(--text-color) [--hover-bg-color:var(--bg-color)]/80",
  variants: {
    interactive: {
      true: focusRingStyles({
        className: "hover:bg-(--hover-bg-color) focus-visible:bg-(--hover-bg-color) hover:text-(--text-color)",
      }),
    },
    colorScheme: {
      neutral: "",
      dark: "",
      teal: "",
      orange: "",
      blue: "",
      green: "",
      yellow: "",
      red: "",
      purple: "",
      pink: "",
      primary: "",
      secondary: "",
      positive: "",
      negative: "",
      warning: "",
      info: "",
      ai: "",
    },
    variant: {
      subtle: "",
      solid: "",
    },
    shape: {
      rectangle: "rounded-md",
      pill: "rounded-full",
    },
    size: {
      sm: "gap-0.5 text-[0.6875rem] px-2 h-5",
      normal: "gap-1 text-xs px-2.5 h-6",
      lg: "gap-1.5 text-sm px-3 h-8",
    },
  },
  defaultVariants: {
    colorScheme: "neutral",
    variant: "subtle",
    shape: "rectangle",
    size: "normal",
  },
});

export type BadgeVariants = VariantProps<typeof badgeVariants>;

export const badgeIconVariants = tv({
  base: "color-current grid place-items-center",
  variants: {
    size: {
      sm: "w-2.75 h-2.75",
      normal: "w-3.5 h-3.5",
      lg: "w-4 h-4",
    },
  },
  defaultVariants: {
    size: "normal",
  },
});

export const COLOR_SCHEME_VARS = {
  neutral: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-neutral-5)]",
      text: "[--text-color:var(--color-ds-neutral-11)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-neutral-11)]",
      text: "[--text-color:var(--color-ds-neutral-1)]",
    },
  },
  dark: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-neutral-7)]",
      text: "[--text-color:var(--color-ds-neutral-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-neutral-12)]",
      text: "[--text-color:var(--color-ds-neutral-1)]",
    },
  },
  teal: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-teal-4)]",
      text: "[--text-color:var(--color-ds-teal-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-teal-10)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
  orange: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-orange-4)]",
      text: "[--text-color:var(--color-ds-orange-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-orange-10)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
  blue: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-blue-3)]",
      text: "[--text-color:var(--color-ds-blue-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-blue-11)] dark:[--bg-color:var(--color-ds-blue-10)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
  green: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-green-3)]",
      text: "[--text-color:var(--color-ds-green-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-green-11)] dark:[--bg-color:var(--color-ds-green-10)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
  yellow: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-yellow-3)]",
      text: "[--text-color:var(--color-ds-yellow-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-yellow-9)] dark:[--bg-color:var(--color-ds-yellow-10)]",
      text: "[--text-color:var(--color-ds-neutral-12)] dark:[--text-color:var(--color-ds-neutral-1)]",
    },
  },
  red: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-red-5)]",
      text: "[--text-color:var(--color-ds-red-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-red-11)] dark:[--bg-color:var(--color-ds-red-9)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
  purple: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-purple-5)]",
      text: "[--text-color:var(--color-ds-purple-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-purple-9)] dark:[--bg-color:var(--color-ds-purple-10)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
  pink: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-pink-5)]",
      text: "[--text-color:var(--color-ds-pink-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-pink-9)] dark:[--bg-color:var(--color-ds-pink-10)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
  // Semantic colors
  primary: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-accent-primary-4)]",
      text: "[--text-color:var(--color-ds-accent-primary-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-accent-primary-11)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
  secondary: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-accent-secondary-4)]",
      text: "[--text-color:var(--color-ds-accent-secondary-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-accent-secondary-10)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
  positive: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-green-3)]",
      text: "[--text-color:var(--color-ds-green-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-green-11)] dark:[--bg-color:var(--color-ds-green-10)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
  negative: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-red-5)]",
      text: "[--text-color:var(--color-ds-red-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-red-11)] dark:[--bg-color:var(--color-ds-red-9)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
  warning: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-yellow-3)]",
      text: "[--text-color:var(--color-ds-yellow-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-yellow-9)] dark:[--bg-color:var(--color-ds-yellow-10)]",
      text: "[--text-color:var(--color-ds-neutral-12)] dark:[--text-color:var(--color-ds-neutral-1)]",
    },
  },
  info: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-blue-3)]",
      text: "[--text-color:var(--color-ds-blue-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-blue-11)] dark:[--bg-color:var(--color-ds-blue-10)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
  ai: {
    subtle: {
      bg: "[--bg-color:var(--color-ds-purple-5)]",
      text: "[--text-color:var(--color-ds-purple-12)]",
    },
    solid: {
      bg: "[--bg-color:var(--color-ds-purple-9)] dark:[--bg-color:var(--color-ds-purple-10)]",
      text: "[--text-color:var(--color-white)]",
    },
  },
} as const;
